
import { useState } from 'react';
import { callToolAgent, getCurrentBillingPlan } from '@/utils/mcp/toolOrchestrator';
import { toast } from 'sonner';
import { withTimeout, getErrorMessage } from '@/utils/errorHandling'; // Added getErrorMessage
import { supabase } from "@/integrations/supabase/client";

// Set different timeouts for different agents to implement bulkhead isolation
const AGENT_TIMEOUTS: Record<string, number> = {
  'microsoft-exchange': 10000, // 10 seconds
  'linkedin': 12000, // 12 seconds
  'elevenlabs': 15000, // 15 seconds
  'default': 8000 // 8 seconds default
};

type ToolAgentMethod<T> = (params?: Record<string, any>) => Promise<T>;

interface ToolAgentResponse<T> {
  callMethod: <R = T>(method: string, params?: Record<string, any>) => Promise<R | null>;
  isLoading: boolean;
  error: Error | null;
  data: T | null;
}

/**
 * A custom hook to interact with MCP tool agents with bulkhead pattern
 * to isolate failures and prevent cascading failures
 */
export function useMCPToolAgent<T = Record<string, any>>(toolName: string): ToolAgentResponse<T> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<T | null>(null);

  /**
   * Call a method on the tool agent with timeout protection (bulkhead pattern)
   */
  const callMethod = async <R = T>(method: string, params: Record<string, any> = {}): Promise<R | null> => {
    setIsLoading(true);
    setError(null);
    
    const startTime = Date.now();
    let userId = null;
    try {
      const user = await supabase.auth.getUser();
      userId = user?.data?.user?.id || null;
    } catch (e) {
      console.warn("Could not get user for alert logging in useMCPToolAgent");
    }

    try {
      // Get timeout for this agent
      const timeout = AGENT_TIMEOUTS[toolName] || AGENT_TIMEOUTS.default;
      
      // Call the tool agent with timeout protection
      const result = await withTimeout(
        callToolAgent<R>(toolName, method, params),
        timeout,
        `Request to ${toolName}.${method} timed out after ${timeout}ms`
      );
      
      setData(result as unknown as T);
      
      // Log metrics for successful operation
      logOperationMetrics(toolName, method, Date.now() - startTime, 'success', { userId });
      
      return result;
    } catch (err) {
      const errorMessage = getErrorMessage(err); // Use getErrorMessage
      const errorObj = err instanceof Error ? err : new Error(errorMessage);
      setError(errorObj);
      toast.error(`Error with ${toolName}: ${errorMessage}`);
      
      // Log metrics for failed operation
      logOperationMetrics(
        toolName, 
        method, 
        Date.now() - startTime, 
        'error',
        { errorMessage, params, error: errorObj, userId }
      );

      // Log system alert
      try {
        await supabase.functions.invoke('log-system-alert', {
          body: {
            source: `mcp_tool_agent:${toolName}`,
            type: errorMessage.toLowerCase().includes('timed out') ? 'AGENT_TIMEOUT' : 'AGENT_API_ERROR',
            severity: 'ERROR',
            message: `Error in ${toolName}.${method}: ${errorMessage}`,
            details: { method, params, error: { name: errorObj.name, message: errorObj.message, stack: errorObj.stack } },
            user_id: userId
          }
        });
      } catch (alertError) {
        console.error("Failed to log system alert for MCP tool agent error:", alertError);
      }
      
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    callMethod,
    isLoading,
    error,
    data
  };
}

/**
 * Log operation metrics for dashboard visualization
 */
async function logOperationMetrics(
  targetAgent: string,
  operation: string,
  latencyMs: number,
  status: 'success' | 'error',
  metadata?: Record<string, any>
) {
  try {
    // Add user_id to metadata if not already present
    if (metadata && !metadata.user_id) {
      try {
        const user = await supabase.auth.getUser();
        metadata.user_id = user?.data?.user?.id || 'anonymous_mcp_metric';
      } catch (e) {
        metadata.user_id = 'anonymous_mcp_metric_error_user';
      }
    } else if (!metadata) {
      metadata = {};
       try {
        const user = await supabase.auth.getUser();
        metadata.user_id = user?.data?.user?.id || 'anonymous_mcp_metric_no_meta';
      } catch (e) {
        metadata.user_id = 'anonymous_mcp_metric_error_user_no_meta';
      }
    }


    await supabase.functions.invoke('log-dashboard-metrics', {
      body: {
        target_agent: targetAgent,
        operation,
        latency_ms: latencyMs,
        status,
        metadata // metadata now includes user_id
      }
    });
  } catch (error) {
    console.error('Failed to log metrics:', error);
    // Don't throw, as this is non-critical
  }
}

/**
 * Microsoft Exchange tool agent hook
 */
export function useMicrosoftExchangeAgent() {
  const toolAgent = useMCPToolAgent('microsoft-exchange');
  
  return {
    ...toolAgent,
    listEmails: (params?: Record<string, any>) => 
      toolAgent.callMethod<Record<string, any>>('listEmails', params),
    sendEmail: (to: string, subject: string, body: string) => 
      toolAgent.callMethod<Record<string, any>>('sendEmail', { to, subject, body }),
    getCalendarEvents: (params?: Record<string, any>) => 
      toolAgent.callMethod<Record<string, any>>('getCalendarEvents', params),
    createCalendarEvent: (title: string, start: string, end: string, rest?: Record<string, any>) => 
      toolAgent.callMethod<Record<string, any>>('createCalendarEvent', { title, start, end, ...rest }),
    getCalendars: () => 
      toolAgent.callMethod<Record<string, any>>('getCalendars')
  };
}

/**
 * LinkedIn tool agent hook
 */
export function useLinkedInAgent() {
  const toolAgent = useMCPToolAgent('linkedin');
  
  return {
    ...toolAgent,
    getProfile: (profileId: string) => 
      toolAgent.callMethod<Record<string, any>>('getProfile', { profileId }),
    searchJobs: (params: Record<string, any>) => 
      toolAgent.callMethod<Record<string, any>>('searchJobs', params),
    searchProfiles: (params: Record<string, any>) => 
      toolAgent.callMethod<Record<string, any>>('searchProfiles', params),
    sendMessage: (recipientId: string, message: string) => 
      toolAgent.callMethod<Record<string, any>>('sendMessage', { recipientId, message })
  };
}

/**
 * ElevenLabs tool agent hook
 */
export function useElevenLabsAgent() {
  const toolAgent = useMCPToolAgent('elevenlabs');
  
  return {
    ...toolAgent,
    textToSpeech: (text: string, voiceId?: string, model?: string) => 
      toolAgent.callMethod<Record<string, any>>('textToSpeech', { text, voiceId, model }),
    getVoices: () => 
      toolAgent.callMethod<Record<string, any>>('getVoices'),
    getModels: () => 
      toolAgent.callMethod<Record<string, any>>('getModels')
  };
}
